import { useState, useEffect, useRef, useCallback } from "react";
import { 
  ShoppingCart, Plus, Minus, Trash2, Search, DollarSign,
  CreditCard, Banknote, Smartphone, CheckCircle, Loader2,
  Package, User, Phone, FileText, Receipt, Printer, Download
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const paymentMethods = [
  { value: "Cash", label: "Cash", icon: Banknote },
  { value: "Bank Transfer", label: "Bank Transfer", icon: CreditCard },
  { value: "Mobile Money", label: "Mobile Money", icon: Smartphone },
  { value: "Card", label: "Card Payment", icon: CreditCard },
];

const paymentDestinations = [
  { value: "cash", label: "Cash at Office" },
  { value: "bank", label: "Bank Account" },
  { value: "mobile_money", label: "Mobile Money" },
];

export default function QuickSale() {
  const { t } = useLanguage();
  const [inventoryItems, setInventoryItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [completedSale, setCompletedSale] = useState(null);
  const [printingReceipt, setPrintingReceipt] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const receiptRef = useRef(null);
  const lastAddedRef = useRef({ id: null, timestamp: 0 });
  const isUpdatingRef = useRef(false);
  
  const [checkoutForm, setCheckoutForm] = useState({
    payment_method: "Cash",
    payment_destination: "cash",
    customer_name: "",
    customer_phone: "",
    notes: ""
  });

  useEffect(() => {
    loadInventoryItems();
    loadBusinessProfile();
  }, []);
  
  const loadBusinessProfile = async () => {
    try {
      const response = await axios.get(`${API}/business-profile`);
      setBusinessProfile(response.data);
    } catch (error) {
      console.error("Failed to load business profile:", error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchItems(searchQuery);
      } else {
        loadInventoryItems();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadInventoryItems = async () => {
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/inventory/items-for-sale`);
      setInventoryItems(response.data);
    } catch (error) {
      console.error("Failed to load inventory:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const searchItems = async (query) => {
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/inventory/items-for-sale?search=${encodeURIComponent(query)}`);
      setInventoryItems(response.data);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const addToCart = useCallback((item, e) => {
    // Prevent event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Debounce rapid clicks - use a longer timeout for React StrictMode
    const now = Date.now();
    const lastAdd = lastAddedRef.current;
    
    // If same item clicked within 500ms, skip (handles StrictMode double-invoke)
    if (lastAdd.id === item.id && now - lastAdd.timestamp < 500) {
      return;
    }
    
    // Update tracking ref immediately BEFORE state update
    lastAddedRef.current = { id: item.id, timestamp: now };
    
    setCart(prevCart => {
      const existingIndex = prevCart.findIndex(c => c.inventory_item_id === item.id);
      
      if (existingIndex >= 0) {
        const cartItem = prevCart[existingIndex];
        const newQuantity = cartItem.quantity + 1;
        return prevCart.map((c, idx) => 
          idx === existingIndex 
            ? {
                ...c,
                quantity: newQuantity,
                amount: newQuantity * c.unit_price
              }
            : c
        );
      } else {
        return [...prevCart, {
          inventory_item_id: item.id,
          name: item.name,
          quantity: 1,
          unit_price: item.selling_price || 0,
          amount: item.selling_price || 0,
          available_qty: item.current_quantity,
          unit: item.unit
        }];
      }
    });
    toast.success(`Added ${item.name} to cart`);
  }, []);

  const updateQuantity = useCallback((index, delta, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setCart(prevCart => {
      const currentItem = prevCart[index];
      if (!currentItem) return prevCart;
      
      const newQty = currentItem.quantity + delta;
      
      if (newQty <= 0) {
        return prevCart.filter((_, i) => i !== index);
      } else if (newQty <= currentItem.available_qty || !currentItem.available_qty) {
        return prevCart.map((item, i) => 
          i === index 
            ? { ...item, quantity: newQty, amount: newQty * item.unit_price }
            : item
        );
      } else {
        toast.error("Not enough stock available");
        return prevCart;
      }
    });
  }, []);

  const removeFromCart = (index) => {
    setCart(prevCart => {
      const updated = [...prevCart];
      updated.splice(index, 1);
      return updated;
    });
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.amount, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/sales/quick-cash-sale`, {
        items: cart.map(item => ({
          inventory_item_id: item.inventory_item_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.amount
        })),
        payment_method: checkoutForm.payment_method,
        payment_destination: checkoutForm.payment_destination,
        customer_name: checkoutForm.customer_name,
        customer_phone: checkoutForm.customer_phone,
        notes: checkoutForm.notes
      });
      
      setCompletedSale(response.data);
      setCart([]);
      setShowCheckout(false);
      toast.success("Sale completed!");
      
      // Reload inventory to reflect new quantities
      loadInventoryItems();
    } catch (error) {
      console.error("Checkout failed:", error);
      toast.error(error.response?.data?.detail || "Failed to complete sale");
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount);
  };
  
  const printReceipt = async () => {
    if (!receiptRef.current || !completedSale) return;
    
    setPrintingReceipt(true);
    try {
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false
      });
      
      const imgData = canvas.toDataURL("image/png");
      // Receipt size: 80mm x variable height (thermal printer style)
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, 150]  // 80mm width receipt
      });
      
      const imgWidth = 70; // 80mm - 10mm margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, "PNG", 5, 5, imgWidth, imgHeight);
      pdf.save(`receipt-${completedSale.sale?.sale_number}.pdf`);
      toast.success("Receipt downloaded!");
    } catch (error) {
      console.error("Failed to print receipt:", error);
      toast.error("Failed to print receipt");
    } finally {
      setPrintingReceipt(false);
    }
  };

  return (
    <div className="h-full flex" data-testid="quick-sale-page">
      {/* Left: Product Grid */}
      <div className="flex-1 p-4 overflow-auto">
        <div className="mb-4">
          <h1 className="text-2xl font-bold mb-2">Quick Cash Sale</h1>
          <p className="text-sm text-muted-foreground">Select items for walk-in customers</p>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search products by name or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="product-search"
          />
        </div>

        {/* Product Grid */}
        {searchLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : inventoryItems.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No inventory items found</p>
            <p className="text-sm mt-1">Add items in Inventory to use Quick Sale</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {inventoryItems.map((item) => (
              <button
                key={item.id}
                onClick={(e) => addToCart(item, e)}
                className="p-4 bg-card border rounded-lg hover:border-primary hover:shadow-md transition-all text-left group"
                data-testid={`product-${item.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <Package className="w-8 h-8 text-primary/60 group-hover:text-primary" />
                  {item.track_quantity && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      item.current_quantity > 0 
                        ? "bg-emerald-100 text-emerald-700" 
                        : "bg-red-100 text-red-700"
                    }`}>
                      {item.current_quantity} {item.unit}
                    </span>
                  )}
                </div>
                <p className="font-medium text-sm line-clamp-2">{item.name}</p>
                {item.sku && <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>}
                <p className="text-lg font-bold text-primary mt-2">
                  {formatCurrency(item.selling_price || 0)}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Right: Cart */}
      <div className="w-80 lg:w-96 bg-card border-l flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Cart
            </h2>
            <span className="text-sm text-muted-foreground">
              {cartItemCount} item{cartItemCount !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {cart.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Cart is empty</p>
              <p className="text-xs">Click products to add</p>
            </div>
          ) : (
            cart.map((item, index) => (
              <div key={index} className="bg-muted/30 rounded-lg p-3" data-testid={`cart-item-${index}`}>
                <div className="flex justify-between items-start mb-2">
                  <p className="font-medium text-sm line-clamp-1 flex-1">{item.name}</p>
                  <button
                    onClick={() => removeFromCart(index)}
                    className="text-muted-foreground hover:text-red-500 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => updateQuantity(index, -1, e)}
                      className="w-7 h-7 rounded-full bg-background border flex items-center justify-center hover:bg-muted"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <button
                      onClick={(e) => updateQuantity(index, 1, e)}
                      className="w-7 h-7 rounded-full bg-background border flex items-center justify-center hover:bg-muted"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="font-semibold">{formatCurrency(item.amount)}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  @ {formatCurrency(item.unit_price)} each
                </p>
              </div>
            ))
          )}
        </div>

        {/* Cart Footer */}
        <div className="p-4 border-t space-y-3">
          <div className="flex justify-between items-center text-lg font-bold">
            <span>Total</span>
            <span className="text-primary">{formatCurrency(cartTotal)}</span>
          </div>
          
          <Button
            className="w-full gap-2"
            size="lg"
            onClick={() => setShowCheckout(true)}
            disabled={cart.length === 0}
            data-testid="checkout-btn"
          >
            <DollarSign className="w-5 h-5" />
            Checkout
          </Button>
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Sale</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Order Summary */}
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-sm text-muted-foreground mb-1">{cart.length} items</p>
              <p className="text-2xl font-bold">{formatCurrency(cartTotal)}</p>
            </div>

            {/* Payment Method */}
            <div>
              <Label>Payment Method</Label>
              <Select 
                value={checkoutForm.payment_method} 
                onValueChange={(v) => setCheckoutForm({...checkoutForm, payment_method: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(method => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Payment Destination */}
            <div>
              <Label>Deposit To</Label>
              <Select 
                value={checkoutForm.payment_destination} 
                onValueChange={(v) => setCheckoutForm({...checkoutForm, payment_destination: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentDestinations.map(dest => (
                    <SelectItem key={dest.value} value={dest.value}>{dest.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Info (Optional) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Customer Name (optional)</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={checkoutForm.customer_name}
                    onChange={(e) => setCheckoutForm({...checkoutForm, customer_name: e.target.value})}
                    placeholder="Walk-in"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Phone (optional)</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={checkoutForm.customer_phone}
                    onChange={(e) => setCheckoutForm({...checkoutForm, customer_phone: e.target.value})}
                    placeholder="Phone"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                value={checkoutForm.notes}
                onChange={(e) => setCheckoutForm({...checkoutForm, notes: e.target.value})}
                placeholder="Any notes..."
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Accounting Entry Preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              <p className="font-medium mb-1">Accounting Entries:</p>
              <p>Dr {checkoutForm.payment_destination === 'cash' ? 'Cash' : checkoutForm.payment_destination === 'bank' ? 'Bank' : 'Mobile Money'}: {formatCurrency(cartTotal)}</p>
              <p>Cr Sales Revenue: {formatCurrency(cartTotal)}</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCheckout(false)}>
              Cancel
            </Button>
            <Button onClick={handleCheckout} disabled={processing} className="gap-2">
              {processing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Complete Sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sale Complete Dialog */}
      <Dialog open={!!completedSale} onOpenChange={() => setCompletedSale(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <CheckCircle className="w-6 h-6" />
              Sale Complete!
            </DialogTitle>
          </DialogHeader>
          
          {completedSale && (
            <div className="space-y-4">
              {/* Receipt Preview - Hidden for PDF generation */}
              <div 
                ref={receiptRef} 
                className="bg-white p-4 border rounded-lg"
                style={{ fontFamily: "'Courier New', monospace" }}
              >
                {/* Header */}
                <div className="text-center mb-4">
                  {businessProfile?.logo_data && (
                    <img 
                      src={businessProfile.logo_data} 
                      alt="Logo" 
                      className="h-10 mx-auto mb-2 object-contain"
                    />
                  )}
                  <p className="font-bold text-lg">{businessProfile?.business_name || "LedgerAMT"}</p>
                  {businessProfile?.address && (
                    <p className="text-xs text-gray-600">{businessProfile.address}</p>
                  )}
                  {businessProfile?.phone && (
                    <p className="text-xs text-gray-600">Tel: {businessProfile.phone}</p>
                  )}
                </div>
                
                <div className="border-t border-dashed border-gray-300 my-2"></div>
                
                {/* Receipt Details */}
                <div className="text-xs space-y-1 mb-3">
                  <div className="flex justify-between">
                    <span>Receipt #:</span>
                    <span className="font-bold">{completedSale.sale?.sale_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Date:</span>
                    <span>{new Date(completedSale.sale?.sale_date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Time:</span>
                    <span>{new Date().toLocaleTimeString()}</span>
                  </div>
                  {completedSale.sale?.customer_name && (
                    <div className="flex justify-between">
                      <span>Customer:</span>
                      <span>{completedSale.sale.customer_name}</span>
                    </div>
                  )}
                </div>
                
                <div className="border-t border-dashed border-gray-300 my-2"></div>
                
                {/* Items */}
                <div className="text-xs space-y-2 mb-3">
                  {completedSale.sale?.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <div className="flex-1">
                        <p className="font-medium">{item.name}</p>
                        <p className="text-gray-500">
                          {item.quantity} x {formatCurrency(item.unit_price)}
                        </p>
                      </div>
                      <span className="font-medium">{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-dashed border-gray-300 my-2"></div>
                
                {/* Totals */}
                <div className="text-sm space-y-1">
                  <div className="flex justify-between font-bold text-base">
                    <span>TOTAL</span>
                    <span>{formatCurrency(completedSale.sale?.total || 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Payment:</span>
                    <span>{completedSale.sale?.payment_method}</span>
                  </div>
                </div>
                
                <div className="border-t border-dashed border-gray-300 my-3"></div>
                
                {/* Footer */}
                <div className="text-center text-xs text-gray-500">
                  <p>Thank you for your purchase!</p>
                  <p className="mt-1">Powered by LedgerAMT</p>
                </div>
              </div>

              <div className="pt-2 flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={printReceipt}
                  disabled={printingReceipt}
                >
                  {printingReceipt ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download Receipt
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => setCompletedSale(null)}
                >
                  New Sale
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
